import React, { useState, useEffect } from 'react';
import api from '../config/axios';

const DashboardPage = () => {
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchDatasets = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/models');
      
      // Ensure response.data is an array
      const data = Array.isArray(response.data) ? response.data : [];
      setDatasets(data);
      setError('');
    } catch (error) {
      console.error('Error fetching datasets:', error);
      
      // If it's a 401 error, the user will be redirected to login
      if (error.response?.status === 401) {
        setError('Authentication required. Please log in.');
      } else if (error.code === 'ERR_NETWORK') {
        setError('Unable to connect to server. Please check your connection.');
        // Use fallback data for development
        setDatasets([
          { name: 'cough_dataset', files: ['cough_data.json', 'cough_model.bin'] },
          { name: 'skin_dataset', files: ['skin_data.json', 'skin_model.bin'] },
          { name: 'xray_chest', files: ['chest_data.json', 'chest_model.bin'] },
          { name: 'xray_hand', files: ['hand_data.json'] },
          { name: 'xray_leg', files: ['leg_data.json', 'leg_model.bin'] },
          { name: 'xray_spine', files: ['spine_data.json', 'spine_model.bin'] }
        ]);
      } else {
        setError('Failed to fetch datasets. Please try again later.');
        // Set empty array as fallback
        setDatasets([]);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDatasets();
  }, []);

  const hasModelFiles = (files) => {
    if (!Array.isArray(files)) return false;
    return files.some(file => file.endsWith('.json')) && 
           files.some(file => file.endsWith('.bin'));
  };

  // Ensure datasets is always an array
  const safeDatasets = Array.isArray(datasets) ? datasets : [];

  return (
    <div className="page-content">
      <h2>Dashboard Overview</h2>
      
      {error && (
        <div className="alert alert-error">
          {error}
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card">
          <h3>Total Datasets</h3>
          <p className="stat-number">{loading ? '...' : safeDatasets.length}</p>
        </div>
        <div className="stat-card">
          <h3>Active Models</h3>
          <p className="stat-number">
            {loading ? '...' : safeDatasets.filter(dataset => hasModelFiles(dataset.files)).length}
          </p>
        </div>
        <div className="stat-card">
          <h3>Total Files</h3>
          <p className="stat-number">
            {loading ? '...' : safeDatasets.reduce((total, dataset) => {
              const files = Array.isArray(dataset.files) ? dataset.files : [];
              return total + files.length;
            }, 0)}
          </p>
        </div>
      </div>

      <h3>Recent Datasets</h3>
      {loading ? (
        <p>Loading datasets...</p>
      ) : safeDatasets.length === 0 ? (
        <p>No datasets found. Upload some model files to get started.</p>
      ) : (
        <div className="datasets-grid">
          {safeDatasets.slice(0, 6).map((dataset) => (
            <div key={dataset.name} className="dataset-card">
              <div className="dataset-name">
                {dataset.name}
              </div>
              
              {!hasModelFiles(dataset.files) && (
                <div className="alert alert-warning">
                  ⚠️ Missing model files
                </div>
              )}
              
              <div className="dataset-files">
                {Array.isArray(dataset.files) ? dataset.files.length : 0} files
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default DashboardPage; 